import { useState, useEffect } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { useTheme } from "@/hooks/useTheme";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_aa20eee5-f3ca-4771-aefa-339a7569da1e/artifacts/qkx859ux_RESQ%20logo%20kolor%4010x.png";
const GRUPAQ_LOGO_URL = "https://customer-assets.emergentagent.com/job_029f614f-0bc6-470f-b667-169a8ae993b6/artifacts/gvewpsw9_GrupaQ-white.png";

const navItems = [
  { label: "Serwis", href: "/zgloszenie", isLink: true },
  { label: "Współpraca", href: "#wspolpraca" },
  { label: "O nas", href: "#o-nas" },
  { label: "Oferta", href: "#uslugi" },
  { label: "Partnerzy", href: "#partnerzy" },
  { label: "Kontakt", href: "#kontakt" },
];

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinkClass = "text-[#212738] hover:text-[#EA8628] dark:text-white/80 dark:hover:text-white";

  const mobileLinkClass = "text-[#212738] hover:text-[#EA8628] dark:text-white/80 dark:hover:text-white";

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass-nav border-b border-gray-200 dark:border-white/10"
          : "bg-white/70 dark:bg-transparent backdrop-blur-sm"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo ResQ */}
          <a href="#" className="flex items-center flex-shrink-0" data-testid="nav-logo">
            <img
              src={LOGO_URL}
              alt="ResQ Logo"
              className="h-10 lg:h-14 w-auto transition-transform duration-200 hover:scale-105"
            />
          </a>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center justify-center flex-1 gap-6">
            {navItems.map((item) =>
              item.isLink ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`transition-colors duration-200 text-base font-medium link-underline ${navLinkClass}`}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className={`transition-colors duration-200 text-base font-medium link-underline ${navLinkClass}`}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  {item.label}
                </a>
              )
            )}
          </div>

          {/* Right side: theme toggle + GrupaQ logo */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full transition-colors duration-200 text-[#212738] hover:text-[#EA8628] hover:bg-gray-100 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10"
              data-testid="theme-toggle-button"
              aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <a
              href="https://grupaq.pl/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
              data-testid="nav-grupaq-logo"
            >
              <img
                src={GRUPAQ_LOGO_URL}
                alt="GrupaQ Logo"
                className={`h-12 lg:h-14 w-auto transition-all duration-200 hover:scale-105 ${
                  theme !== "dark" ? "brightness-0" : ""
                }`}
              />
            </a>
          </div>

          {/* Mobile: theme toggle + Menu Button */}
          <div className="flex lg:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full transition-colors duration-200 text-[#212738] hover:text-[#EA8628] dark:text-white/70 dark:hover:text-white"
              data-testid="theme-toggle-button-mobile"
              aria-label={theme === "dark" ? "Przełącz na jasny motyw" : "Przełącz na ciemny motyw"}
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button
              className="p-2 text-[#212738] dark:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden bg-white dark:bg-[#0D0F12] border-t border-gray-200 dark:border-white/10"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-4 space-y-3">
            {navItems.map((item) =>
              item.isLink ? (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block py-2 text-[#212738]/80 hover:text-[#EA8628] dark:text-white/80 dark:hover:text-white transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  key={item.href}
                  href={item.href}
                  className="block py-2 text-[#212738]/80 hover:text-[#EA8628] dark:text-white/80 dark:hover:text-white transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              )
            )}
            {/* GrupaQ logo in mobile menu */}
            <div className="pt-3 border-t border-gray-200 dark:border-white/10">
              <a
                href="https://grupaq.pl/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 py-2"
                data-testid="mobile-grupaq-logo"
              >
                <img
                  src={GRUPAQ_LOGO_URL}
                  alt="GrupaQ Logo"
                  className={`h-10 w-auto ${theme !== "dark" ? "brightness-0" : ""}`}
                />
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
